/* eslint-disable function-paren-newline */
import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchReserhubUser,
  fetchReserhubAccountNextPurchase,
  fetchConnectedAccountByProvider,
  clearAllReserhubData,
  invalidateReserhubSession,
  currentReserhubSession,
  currentUserRequest,
} from '@/actions/reserhubAccount';
import getPurchaseStepName from 'utils/purchase/getPurchaseStepName';
import { setAlert } from '@/actions';
import usePurchase from 'hooks/store/usePurchase';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useGrowthBookFeatureValue from 'components/GrowthBookProvider/useGrowthBookFeatureValue';
import { isUnifiedAccountEnabledForClient } from 'utils/isReserhubUnifiedAccountEnabled';
import errorMonitoring from 'errorMonitoring';
import { setConfig as setApiConfig } from 'js-api-client';
import { getCommerceApiClient, setAuthToken } from '../services/commerce-api/commerceApiClient';
import { CurrentLang } from '../utils/lang';
import { isNativeAppWebView } from '../utils/webview';
import { takeHeldNativeAuthToken } from '../utils/nativeAuthToken';
import ReserhubDotersUnavailableDialog from '../components/reserhub/ReserhubDotersUnavailableDialog';

const ReserhubAuthContext = createContext();
const RESERHUB_LOGIN_FLAG = 'RESERHUB_USER_LOGGED_IN';

/** Message the native host posts when its session starts or ends. */
const NATIVE_SESSION_CHANGED = 'NATIVE_SESSION_CHANGED';

/**
 * Read a `message` event payload as a native host message.
 *
 * The channel is shared with the Mixpanel bridge (`initializers.js`) and with any same-origin
 * `postMessage` traffic, and React Native's Android bridge delivers strings rather than objects, so
 * this normalizes both shapes and returns null for anything it cannot read instead of throwing.
 *
 * @param {*} data - The `data` property of a `message` event.
 * @returns {object|null} The parsed message, or null when it is not readable as one.
 */
function parseNativeMessage(data) {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return data && typeof data === 'object' ? data : null;
}

/**
 * Whether a `message` event's origin is one the native host can produce.
 *
 * The host reaches this page only by running script in it: an injected `window.postMessage` arrives
 * stamped with the page's own origin, and a synthesized `MessageEvent` (React Native's Android
 * bridge, and the tests) arrives with an empty one. Any other value belongs to a different window —
 * a payment or 3DS frame, a widget — which must not be able to install a session credential or end
 * the session.
 *
 * This is not authentication. Same-origin script passes it, as it would past any origin check; it
 * removes the frames on the page, not an XSS.
 *
 * @param {string|undefined} origin - The `origin` property of a `message` event.
 * @returns {boolean} True when the origin could be the native host's.
 */
function isNativeHostOrigin(origin) {
  if (!origin) return true;
  return typeof window !== 'undefined' && origin === window.location.origin;
}

/**
 * Return the redirect URL from a Doters authentication response.
 * @param {Object} response - Commerce API response.
 * @param {string} flow - Flow name used to identify invalid responses.
 * @returns {string} A non-empty redirect URL.
 * @throws {Error} If Commerce API does not return a usable redirect URL.
 */
function getDotersRedirectUrl(response, flow) {
  const url = response?.url;
  if (typeof url !== 'string' || !url.trim()) {
    throw new Error(`Doters ${flow} response has no redirect URL`);
  }
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error(`Doters ${flow} response has no redirect URL`);
  }
  if (parsed.protocol !== 'https:') {
    throw new Error(`Doters ${flow} response has no redirect URL`);
  }
  return url;
}

/**
 * Provider for the ReserhubAuthContext
 * @param {object} props - children.
 * @param {React.ReactNode} props.children - The child components.
 * @returns {React.ReactElement}
 */
export function ReserhubAccountProvider({ children }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.reserhubAccount.get('user'));
  const nextPurchase = useSelector((state) => state.reserhubAccount.get('nextPurchase'));
  const isNextPurchaseLoading = useSelector((state) =>
    state.reserhubAccount.get('isNextPurchaseLoading'),
  );
  const isNextPurchaseFetched = useSelector((state) =>
    state.reserhubAccount.get('isNextPurchaseFetched'),
  );
  const whitelabelEnvs = useWhitelabelEnvs();
  const { account, auth } = whitelabelEnvs.reserhub || {};
  // GrowthBook: unified_account re-runs effects after features load when progressiveRollout is on
  const unifiedAccountFeature = useGrowthBookFeatureValue('unified_account');
  const purchase = usePurchase();
  const isUserLoading = useSelector((state) => state.reserhubAccount.get('isUserLoading'));
  const isUserFetched = useSelector((state) => state.reserhubAccount.get('isUserFetched'));
  const connectedAccounts = useSelector((state) => state.reserhubAccount.get('connectedAccounts'));
  const isConnectedAccountsFetched = useSelector((state) =>
    state.reserhubAccount.get('isConnectedAccountsFetched'),
  );
  const isConnectedAccountsLoading = useSelector((state) =>
    state.reserhubAccount.get('isConnectedAccountsLoading'),
  );
  const showDotersUnavailableDialog = useSelector((state) =>
    state.reserhubAccount.get('showDotersUnavailableDialog'),
  );
  const features = useSelector((state) => state.whitelabelConfig?.features ?? {});
  const location = useLocation();
  const pathname = location.pathname ?? '';
  const userFetched = useRef(false);
  const purchaseState = useRef(null);
  const hasFetchedDotersOnSearch = useRef(false);
  const growthBook = useGrowthBook();

  const isPurchasePath = pathname.includes('purchase');
  const accountEnabled = isUnifiedAccountEnabledForClient(account, {
    unifiedAccountFeature,
    features,
  });

  const hasShownSessionExpiredAlert = useRef(false);

  /**
   * Set the login flag in localStorage
   */
  const setLoginFlag = useCallback(() => {
    localStorage.setItem(RESERHUB_LOGIN_FLAG, 'true');
  }, []);

  /**
   * Remove the login flag from localStorage
   */
  const removeLoginFlag = useCallback(() => {
    localStorage.removeItem(RESERHUB_LOGIN_FLAG);
  }, []);

  /**
   * Check if user was previously logged in
   * @returns {boolean} Whether the user was previously logged in
   */
  const wasUserLoggedIn = useCallback(() => {
    return localStorage.getItem(RESERHUB_LOGIN_FLAG) === 'true';
  }, []);

  /**
   * Show session expired alert
   */
  const showSessionExpiredAlert = useCallback(() => {
    if (!hasShownSessionExpiredAlert.current) {
      hasShownSessionExpiredAlert.current = true;
      dispatch(setAlert('reserhub_session_finished', 'warning'));
    }
  }, [dispatch, hasShownSessionExpiredAlert]);

  /**
   * Fetch user with session handling
   */
  const fetchUserWithSessionHandling = useCallback(async () => {
    // The generation and revision live in the account actions, so every caller of
    // `fetchReserhubUser` is protected, not only this one. They are read here purely to skip this
    // hook's own bookkeeping — without the revision, a refresh superseded by a newer request would
    // see its own `null` result, decide the session had expired, and warn about a session that had
    // in fact just been established.
    const generation = currentReserhubSession();
    try {
      const pending = dispatch(fetchReserhubUser());
      // Assigned synchronously by the thunk, before its first await, so this is that request's own.
      const revision = currentUserRequest();
      const isCurrent = () =>
        generation === currentReserhubSession() && revision === currentUserRequest();

      const fetchedUser = await pending;

      // The thunk stops before committing anything once `isCurrent()` goes false, so there is
      // nothing to undo here — only the local session bookkeeping to skip. Tearing down instead
      // would be wrong: by now a newer login may own the session, and a blanket clear cannot tell
      // it apart from the one that ended.
      if (!isCurrent()) return null;

      if (fetchedUser && fetchedUser.id) {
        // User successfully fetched, set login flag
        setLoginFlag();
        hasShownSessionExpiredAlert.current = false;
      } else if (wasUserLoggedIn()) {
        // User was logged in before but fetch failed/returned no user
        showSessionExpiredAlert();
        removeLoginFlag();
      }

      return fetchedUser;
    } catch (error) {
      // Same staleness check: a fetch that failed for an already-ended session must not warn.
      if (generation !== currentReserhubSession()) return null;

      // If user was previously logged in, show session expired alert
      if (wasUserLoggedIn()) {
        showSessionExpiredAlert();
        removeLoginFlag();
      }
      throw error;
    }
  }, [
    dispatch,
    setLoginFlag,
    hasShownSessionExpiredAlert,
    removeLoginFlag,
    wasUserLoggedIn,
    showSessionExpiredAlert,
  ]);

  /**
   * Logout the user and clear login flag
   */
  async function logout() {
    try {
      const redirectUrl = encodeURIComponent(window.location.href);
      window.location.href = `/auth/logout?redirectUrl=${redirectUrl}`;
      removeLoginFlag();
      hasShownSessionExpiredAlert.current = false;
    } catch (error) {
      // Even if logout fails, remove the flag
      removeLoginFlag();
      hasShownSessionExpiredAlert.current = false;
      throw error;
    }
  }

  /**
   * Login with Doters
   * @returns {Promise<string>} The redirect URL for Doters login
   * @throws Rejects on API failure after surfacing error via setAlert
   */
  async function loginWithDoters() {
    try {
      const currentLanguage = CurrentLang();
      const language =
        currentLanguage === 'es-MX' || currentLanguage === 'en-US' ? currentLanguage : undefined;
      const response = await getCommerceApiClient().accounts.loginWithDoters({
        language,
        redirectUrl: encodeURIComponent(window.location.href),
      });
      return getDotersRedirectUrl(response, 'login');
    } catch (error) {
      errorMonitoring.notify({
        error,
        info: {
          message: 'Error logging in with doters using unified account',
        },
      });
      dispatch(setAlert('reserhub_login_doters_error', 'warning'));
      throw error;
    }
  }

  /**
   * Initiate Doters reconnection using the appropriate flow:
   * - If Doters is an allowed auth method, use the login flow
   * - If Doters is only a connectable provider, use the connect flow
   * @returns {Promise<string>} The redirect URL
   * @throws Rejects on API failure, an invalid API response, or unsupported tenant configuration
   */
  async function initiateDotersConnection() {
    const allowedAuthMethods = auth?.allowedAuthMethods ?? [];
    const connectableProviders = auth?.connectableProviders ?? [];

    if (allowedAuthMethods.length === 0 || allowedAuthMethods.includes('doters')) {
      return loginWithDoters();
    }

    if (connectableProviders.includes('doters')) {
      try {
        const response = await getCommerceApiClient().accounts.initiateAccountConnection({
          provider: 'doters',
          redirectUrl: encodeURIComponent(window.location.href),
        });
        return getDotersRedirectUrl(response, 'connection');
      } catch (error) {
        errorMonitoring.notify({
          error,
          info: {
            message: 'Error initiating Doters connection using unified account',
          },
        });
        dispatch(setAlert('reserhub_login_doters_error', 'warning'));
        throw error;
      }
    }

    const configError = new Error(
      'Doters is not configured as an auth method or connectable provider',
    );
    errorMonitoring.notify({
      error: configError,
      info: {
        message: 'Doters is not configured for unified account authentication or connection',
      },
    });
    throw configError;
  }

  /**
   * Install a boot token that was held pending the rollout decision.
   *
   * A progressive-rollout tenant's token is consumed from `window` at boot but withheld from the
   * accounts client until GrowthBook resolves, so an excluded visitor never authenticates anything.
   * This is the first moment the answer is known to be yes. Declared ahead of the fetch effects so
   * it runs first on the render where `accountEnabled` turns true, before any account request.
   */
  useEffect(() => {
    if (!accountEnabled) return;

    const heldToken = takeHeldNativeAuthToken();
    if (heldToken) setAuthToken(heldToken);
  }, [accountEnabled]);

  /**
   * Guard so users out of the experiment do not keep session cookies on API requests.
   * Only after GrowthBook has loaded features (ready); before that unifiedAccountFeature may be stale.
   */
  useEffect(() => {
    if (account?.progressiveRollout !== true || !growthBook?.ready) return;

    if (!accountEnabled) {
      setApiConfig({
        withCredentials: false,
      });
      // The rollout has answered no, so the token held at boot is never going to be installed.
      // Drop it rather than leave a Stytch session token sitting in memory for the page's lifetime.
      takeHeldNativeAuthToken();
      // The bearer goes with the cookies. Boot adopts the token assuming a progressiveRollout tenant
      // is enabled, because GrowthBook has not loaded yet; when it resolves the other way the
      // credential must be dropped too, or the commerce client keeps attaching it to every request —
      // including commerce paths such as installments validation that never pass the account gate.
      setAuthToken(null);
    }
  }, [account, accountEnabled, growthBook?.ready]);

  // Fetch next purchase after user is set
  useEffect(() => {
    if (user && user.id) {
      setLoginFlag();
      hasShownSessionExpiredAlert.current = false;
      if (!isNextPurchaseFetched && !isNextPurchaseLoading) {
        dispatch(fetchReserhubAccountNextPurchase());
      }
    }
  }, [
    user,
    dispatch,
    isNextPurchaseFetched,
    isNextPurchaseLoading,
    setLoginFlag,
    hasShownSessionExpiredAlert,
  ]);

  /**
   * Fetch user if purchase is fetched and the current page is a purchase page.
   * Refresh user if the purchase state is completed and the user is not fetched or the purchase state has changed.
   */
  useEffect(() => {
    if (!accountEnabled) return;

    if (purchase?.state !== 'completed') {
      purchaseState.current = purchase?.state;
    }

    if (
      purchase?.token &&
      isPurchasePath &&
      (!userFetched.current ||
        (purchase?.state !== purchaseState.current && purchase?.state === 'completed'))
    ) {
      const isPurchaseJustCompleted =
        purchase?.state !== purchaseState.current && purchase?.state === 'completed';
      purchaseState.current = purchase?.state;
      userFetched.current = true;
      fetchUserWithSessionHandling();

      if (isPurchaseJustCompleted) {
        dispatch(fetchReserhubAccountNextPurchase());
      }
    }
  }, [
    purchase.token,
    purchase.state,
    accountEnabled,
    isPurchasePath,
    fetchUserWithSessionHandling,
    userFetched,
    dispatch,
  ]);

  /**
   * Fetch user if the current page is not a purchase page and the user is not fetched
   */
  useEffect(() => {
    if (!accountEnabled) return;

    if (!isPurchasePath && !userFetched.current) {
      userFetched.current = true;
      fetchUserWithSessionHandling();
    }
  }, [accountEnabled, isPurchasePath, fetchUserWithSessionHandling, userFetched]);

  /**
   * Apply native host session changes in place.
   *
   * The shopper may be mid-checkout, so login and logout are reflected without navigating or
   * reloading. `clearAllReserhubData` is what clears `RESERHUB_LOGIN_FLAG` alongside the user and
   * `userAuthToken`: leaving the flag set would make the next fetch look like an expired session and
   * show `reserhub_session_finished` for what was a deliberate logout.
   *
   * A `token` on the message is optional. When present it is installed on the accounts client before
   * the refetch, so `GET /accounts/auth/me` carries it; when absent the session cookie remains the
   * credential and behavior is unchanged. On logout the token is cleared too — `clearAllReserhubData`
   * only knows about the purchase client's `userAuthToken`, so a stale bearer would otherwise keep
   * authenticating account requests after the session ended.
   *
   * The channel is not authenticated, so a message is honored only when its origin is the page's own
   * or empty — the two shapes the native host can produce — and only when `authenticated` is a
   * boolean. A cross-origin frame can neither install a bearer nor end the session, and a malformed
   * payload cannot be read as a logout.
   */
  useEffect(() => {
    if (!accountEnabled || !isNativeAppWebView()) return undefined;

    /**
     * @param {MessageEvent} event - Incoming `message` event from the native host.
     */
    function handleNativeSessionChange(event) {
      const message = parseNativeMessage(event?.data);
      if (message?.type !== NATIVE_SESSION_CHANGED) return;

      // Only the native host may change the session. A foreign origin is another window on the
      // page — a payment or 3DS frame — and is reported rather than dropped silently, so a bridge
      // that stops matching is visible instead of appearing as a dead login. Checked after the type
      // match so unrelated frame traffic on this shared channel generates no noise.
      if (!isNativeHostOrigin(event?.origin)) {
        errorMonitoring.notify({
          error: new Error('NATIVE_SESSION_CHANGED from an unexpected origin'),
          info: { from: 'ReserhubAccountProvider', origin: event?.origin },
        });
        return;
      }

      // The wire contract is a boolean. An absent flag would otherwise fall into the teardown branch
      // and log a shopper out mid-checkout, and a truthy non-boolean such as the string 'false'
      // would refetch when it meant the opposite.
      if (typeof message.authenticated !== 'boolean') {
        errorMonitoring.notify({
          error: new Error('NATIVE_SESSION_CHANGED without a boolean authenticated flag'),
          info: { from: 'ReserhubAccountProvider', authenticated: typeof message.authenticated },
        });
        return;
      }

      if (message.authenticated) {
        // Hand the session over atomically. Invalidating requests in flight is not enough: the
        // previous session's user, connected accounts, next purchase, saved-card owner,
        // `userAuthToken` and purchase attribution stay committed while the new user resolves, so a
        // checkout could keep showing the previous account's cards and spend its credential in the
        // gap — and a failed resolution would leave that state standing, with the purchase still
        // attributed to the account that is gone. `clearAllReserhubData` invalidates and drops the
        // bearer as well, so it has to run *before* the new token is installed. The explicit
        // invalidation is not redundant: it states the session boundary here, at the point that
        // knows about it, rather than leaving it to a detail of the teardown thunk.
        invalidateReserhubSession();
        hasShownSessionExpiredAlert.current = false;

        // A message that carries no `token` leaves the existing credential in place. One that
        // carries the property with an unusable value clears it instead of being ignored: the
        // previous session's bearer must not authenticate this one, which — with no cookie in the
        // WebView jar — would resolve the old user and attribute this purchase to them.
        const carriesToken = 'token' in message;
        const usableToken =
          typeof message.token === 'string' && message.token ? message.token : null;

        // `forHandover` resets the next-purchase indicators instead of marking them fetched, and
        // awaits the purchase detach — the incoming session must not attribute the same purchase
        // while the previous session's detach is still in flight.
        const teardown = dispatch(clearAllReserhubData({ forHandover: true }));
        const handoverGeneration = currentReserhubSession();

        teardown.then(() => {
          // A newer session message landed while this one was being torn down; it owns the session.
          if (handoverGeneration !== currentReserhubSession()) return;

          if (carriesToken) setAuthToken(usableToken);
          // Already surfaces its own alerts; swallow so a rejected refetch is not unhandled.
          fetchUserWithSessionHandling().catch(() => {});
        });
        return;
      }

      // Token clearing lives in `clearAllReserhubData`, so every teardown path shares it.
      // Invalidating here as well states the session boundary at the point that knows about it,
      // rather than depending on a detail of the teardown thunk.
      invalidateReserhubSession();
      hasShownSessionExpiredAlert.current = false;
      dispatch(clearAllReserhubData());
    }

    window.addEventListener('message', handleNativeSessionChange, false);
    return () => window.removeEventListener('message', handleNativeSessionChange, false);
  }, [accountEnabled, dispatch, fetchUserWithSessionHandling, hasShownSessionExpiredAlert]);

  /**
   * Refresh connected Doters account when on purchase steps (passengers, seats, checkout)
   * or on search results so balance/points are up to date in the UI.
   */
  useEffect(() => {
    const purchaseStep = getPurchaseStepName(pathname);

    // If is a purchase path the doters account should be updated
    const isPurchaseStepThatNeedsDoters =
      purchaseStep === 'passengers' || purchaseStep === 'checkout' || purchaseStep === 'dSeats';

    // If is a purchase step that needs doters, reset the flag because we are not in search results page
    if (isPurchaseStepThatNeedsDoters) hasFetchedDotersOnSearch.current = false;

    // If is a search results path the doters account should be updated if it hasn't been fetched yet at search page
    const isSearchResultsPath = pathname.includes('/search/') && !hasFetchedDotersOnSearch.current;

    // If is a purchase step or a search results path and the doters account should be updated
    const shouldRefreshDoters =
      accountEnabled &&
      user?.id &&
      features.DOTERS_ENABLED &&
      (isPurchaseStepThatNeedsDoters || isSearchResultsPath);

    if (shouldRefreshDoters) {
      dispatch(fetchConnectedAccountByProvider('doters'));
      if (isSearchResultsPath) hasFetchedDotersOnSearch.current = true;
    }

    // This effect will be done just if doters is enabled and the unified account is authenticated (user?.id)
  }, [accountEnabled, dispatch, features.DOTERS_ENABLED, pathname, user?.id]);

  return (
    <ReserhubAuthContext.Provider
      value={{
        user,
        nextPurchase,
        isNextPurchaseLoading,
        isNextPurchaseFetched,
        isUserLoading,
        logout,
        initiateDotersConnection,
        isUserFetched,
        enabled: accountEnabled,
        loginUrl: account?.loginUrl,
        homeUrl: account?.homeUrl,
        connectedAccounts,
        isConnectedAccountsFetched,
        isConnectedAccountsLoading,
        clearAllReserhubData: () => dispatch(clearAllReserhubData()),
      }}
    >
      {children}
      {showDotersUnavailableDialog ? <ReserhubDotersUnavailableDialog /> : null}
    </ReserhubAuthContext.Provider>
  );
}

ReserhubAccountProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to use the ReserhubAuthContext
 * @returns {Object}
 */
export function useReserhubAccount() {
  const context = useContext(ReserhubAuthContext);
  if (!context) {
    throw new Error('useReserhubAccount must be used within a ReserhubAccountProvider');
  }
  return context;
}

/**
 * Hook to use the Reserhub next purchase functionality
 * @returns {Object} Object containing next purchase data and loading states
 */
export function useReserhubAccountNextPurchase() {
  const context = useContext(ReserhubAuthContext);
  if (!context) {
    throw new Error('useReserhubAccountNextPurchase must be used within a ReserhubAccountProvider');
  }
  return {
    nextPurchase: context.nextPurchase,
    isNextPurchaseLoading: context.isNextPurchaseLoading,
    isNextPurchaseFetched: context.isNextPurchaseFetched,
  };
}
